import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { employerApi } from '../services/apiClient';
import { APPLICATIONS_CACHE_KEY } from '../utils/util';
import { useAuthContext } from '../context/AuthContext';
import { useCompanyContext } from '../context/CompanyContext';

const sortApplications = (apps: any[] = []) => {
    return [...apps].sort((a, b) => {
        const aDate = new Date(a?.creationDateTime || 0).getTime();
        const bDate = new Date(b?.creationDateTime || 0).getTime();
        return bDate - aDate;
    });
};

export const useApplications = () => {
    const { currentUser, signOut } = useAuthContext();
    const { activeCompany } = useCompanyContext();
    const [applications, setApplications] = useState<any[]>([]);
    const [isRealtimeLoading, setIsRealtimeLoading] = useState(true);

    // Use activeCompany from CompanyContext instead of currentUser.companyID
    const companyID = activeCompany?.companyID || currentUser?.companyID || currentUser?.company?.companyID;

    const {
        data: initialResponse,
        isLoading: isFetching,
        error,
        refetch
    } = useQuery({
        queryKey: [APPLICATIONS_CACHE_KEY, companyID],
        queryFn: async () => {
            console.log('🔄 useApplications: Fetching applications for company:', companyID);
            const response = await employerApi.get('/applications');
            return response;
        },
        enabled: Boolean(companyID)
    });

    // Reset loading state when company changes
    useEffect(() => {
        if (companyID) {
            console.log('🔄 useApplications: Company changed, resetting loading state for:', companyID);
            setIsRealtimeLoading(true);
            setApplications([]); // Clear applications when company changes
        }
    }, [companyID]);

    // Seed the list with the REST response
    useEffect(() => {
        if (!initialResponse) {
            console.log('🔄 useApplications: No initial response yet');
            return;
        }

        console.log('🔄 useApplications: Processing initial response');
        const data = Array.isArray(initialResponse?.data) ? initialResponse.data : initialResponse?.data?.data;
        if (Array.isArray(data)) {
            console.log('🔄 useApplications: Setting applications, count:', data.length);
            setApplications(sortApplications(data));
            // Don't set isRealtimeLoading to false here - let the realtime listener handle it
        } else {
            console.warn('🔄 useApplications: Response data is not an array:', data);
        }
    }, [initialResponse]);

    // Handle auth failures from the REST call
    useEffect(() => {
        if (!error) return;

        const maybeAxiosError: any = error;
        const status =
            maybeAxiosError?.response?.status ??
            maybeAxiosError?.status ??
            maybeAxiosError?.code;

        if (status === 401) {
            // Clear any cached applications and force logout
            setApplications([]);
            try {
                signOut();
            } catch {
                // Best-effort logout; avoid throwing inside effect
            }
        }
    }, [error, signOut]);

    // Realtime listener per company
    useEffect(() => {
        if (!companyID) {
            console.log('🔄 useApplications: No companyID, skipping realtime listener');
            return;
        }

        console.log('🔄 useApplications: Setting up realtime listener for company:', companyID);
        setIsRealtimeLoading(true); // Reset loading when setting up new listener

        const applicationsRef = collection(firestore, 'applications');
        const companyQuery = query(applicationsRef, where('company.companyID', '==', companyID));

        const unsubscribe = onSnapshot(
            companyQuery,
            (snapshot) => {
                console.log('🔄 useApplications: Realtime snapshot received, docs:', snapshot.docs.length);
                const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                setApplications(sortApplications(docs));
                setIsRealtimeLoading(false);
            },
            (err) => {
                console.error('🔄 useApplications: Realtime listener error', err);
                setIsRealtimeLoading(false);
            }
        );

        return () => {
            console.log('🔄 useApplications: Cleaning up realtime listener for company:', companyID);
            unsubscribe();
        };
    }, [companyID]);

    // Combined loading state: show loading if either REST query or realtime listener is loading
    const isLoading = isRealtimeLoading || isFetching;

    console.log('🔄 useApplications: Return state - isLoading:', isLoading, 'isRealtimeLoading:', isRealtimeLoading, 'isFetching:', isFetching, 'applications:', applications.length);

    return {
        applications,
        isLoading,
        error,
        refetch
    };
};
